/**
 * What the other end said, the last few times.
 *
 * The only record there is: a webhook is fire-and-forget, so a call that was
 * refused leaves no mark anywhere else. The status is the answer most of the
 * time, and the text of the request and response is what is left when there is
 * no status at all -- a connection that never opened has an error instead.
 */
import { useTranslation } from 'react-i18next'

import type { WebhookCall } from '@/api/types'

export function WebhookCalls({ calls }: { calls: readonly WebhookCall[] | undefined }) {
  const { t } = useTranslation()
  const held = calls ?? []

  if (held.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('webhook.noCalls')}</p>
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm">{t('webhook.calls')}</p>
      <ul aria-label={t('webhook.calls')} className="flex flex-col gap-1">
        {held.map((call) => (
          <li key={call.id} className="flex items-center gap-2 font-mono text-xs">
            <span
              className={
                call.response_status !== null &&
                call.response_status !== undefined &&
                call.response_status < 400
                  ? 'text-muted-foreground'
                  : 'text-destructive'
              }
            >
              {/* An error rather than a status when the call never got an
                  answer -- a name that does not resolve has no status code. */}
              {call.response_status ?? call.error ?? '—'}
            </span>
            <span className="truncate">{call.event_type}</span>
            <span className="ml-auto shrink-0 text-muted-foreground">
              {call.called_time ?? ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
